import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { Star, Quote, Plus, X, Send, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [testimonials, setTestimonials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", content: "", rating: 5 });
  const [errors, setErrors] = useState({});

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      setTestimonials([
        {
          id: "1",
          name: "Karolina M.",
          content: "Pan Franek to ikona nauki jazdy sama w sobie. Polecam z całego serca!",
          rating: 5,
        },
        {
          id: "2",
          name: "pa…60",
          content: "Szczerze powiem, że nie wiem, czy można być lepszym instruktorem. Poza tym że tłumaczy to, co powinien, to jeszcze omawia każdą możliwość jaka może się wydarzyć z własnego doświadczenia i takie można powiedzieć triki, które zna wieloletni dobry kierowca. Podejście do człowieka ma zabawne i śmieszne jak kumpel. Zapomnij o stresie nerwach itp. Nawet przy sytuacji gdzie spowodowałbym wypadek - serio. Godziny do uzgodnienia. Nigdy się nie spóźnia. No takiego ze świecą szukać.",
          rating: 5,
        },
        {
          id: "3",
          name: "Marek Halama",
          content: "Mega fajne podejście, fachowa i profesjonalna nauka techniki jazdy, a przy tym zero stresu. Polecam tego instruktora.",
          rating: 5,
        },
      ]);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = "Imię musi mieć minimum 2 znaki";
    }
    if (!formData.content || formData.content.length < 10) {
      newErrors.content = "Opinia musi mieć minimum 10 znaków";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        name: formData.name,
        content: formData.content,
        rating: formData.rating,
      });
      toast.success("Dziękujemy za opinię!", {
        description: "Twoja opinia została dodana.",
      });
      setFormData({ name: "", content: "", rating: 5 });
      setIsModalOpen(false);
      fetchTestimonials();
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      toast.error("Wystąpił błąd", {
        description: "Spróbuj ponownie później.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 cursor-${interactive ? "pointer" : "default"} transition-colors ${
          index < rating ? "text-yellow-400 fill-yellow-400" : "text-slate-300"
        }`}
        onClick={() => interactive && onSelect && onSelect(index + 1)}
      />
    ));
  };

  return (
    <section
      id="opinie"
      className="py-20 lg:py-32 bg-white"
      data-testid="testimonials-section"
      ref={ref}
    >
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-block bg-yellow-400/20 text-slate-900 px-4 py-2 rounded-sm font-heading font-bold text-sm tracking-wider mb-6">
            OPINIE KURSANTÓW
          </div>
          <h2
            className="font-heading font-bold text-4xl lg:text-5xl text-slate-900 tracking-tight mb-6"
            data-testid="testimonials-title"
          >
            CO MÓWIĄ O NAS
            <span className="block text-yellow-500">NASI KURSANCI?</span>
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Opinie naszych absolwentów są najlepszą rekomendacją. Zobacz, co mówią o nas kursanci.
          </p>
          
          {/* Add Review Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="add-testimonial-button"
          >
            <Plus className="w-5 h-5" />
            Dodaj swoją opinię
          </button>
        </motion.div>

        {/* Testimonials Grid */}
        <div
          className="grid md:grid-cols-2 gap-6 lg:gap-8"
          data-testid="testimonials-grid"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-slate-50 border-2 border-slate-100 p-8 rounded-sm relative hover:border-yellow-400 transition-colors group"
              data-testid={`testimonial-card-${index}`}
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                <Quote className="w-16 h-16 text-slate-900" />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-4">{renderStars(testimonial.rating)}</div>

              {/* Content */}
              <p className="text-slate-700 leading-relaxed mb-6 relative z-10">
                "{testimonial.content}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center">
                  <span className="font-heading font-bold text-yellow-400 text-lg">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-heading font-bold text-slate-900 tracking-tight">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-slate-500">Kursant OSK Smreczak</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 bg-slate-900 rounded-sm p-8 lg:p-12"
          data-testid="testimonials-stats"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-heading font-bold text-4xl lg:text-5xl text-yellow-400 mb-2">
                500+
              </div>
              <div className="text-slate-400 text-sm">Przeszkolonych kursantów</div>
            </div>
            <div>
              <div className="font-heading font-bold text-4xl lg:text-5xl text-yellow-400 mb-2">
                Wysoka
              </div>
              <div className="text-slate-400 text-sm">Zdawalność za pierwszym razem</div>
            </div>
            <div>
              <div className="font-heading font-bold text-4xl lg:text-5xl text-yellow-400 mb-2">
                20+
              </div>
              <div className="text-slate-400 text-sm">Lat doświadczenia</div>
            </div>
            <div>
              <div className="font-heading font-bold text-4xl lg:text-5xl text-yellow-400 mb-2">
                5.0
              </div>
              <div className="text-slate-400 text-sm">Średnia ocena</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal for adding testimonial */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}
          data-testid="testimonial-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-sm w-full max-w-lg p-8 relative"
            data-testid="testimonial-modal"
          >
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 transition-colors"
              data-testid="close-modal-button"
            >
              <X className="w-6 h-6" />
            </button>

            <h3 className="font-heading font-bold text-2xl text-slate-900 tracking-tight mb-2">
              Dodaj swoją opinię
            </h3>
            <p className="text-slate-500 mb-6">
              Podziel się swoim doświadczeniem z kursu prawa jazdy.
            </p>

            <form onSubmit={handleSubmit} data-testid="testimonial-form">
              <div className="space-y-5">
                {/* Name Field */}
                <div>
                  <Label htmlFor="testimonial-name" className="font-heading font-semibold text-slate-700 tracking-wide mb-2 block">
                    Imię (lub inicjały) *
                  </Label>
                  <Input
                    id="testimonial-name"
                    type="text"
                    placeholder="np. Anna K."
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-3 border-2 rounded-sm transition-colors ${
                      errors.name ? "border-red-400" : "border-slate-200 focus:border-yellow-400"
                    }`}
                    data-testid="testimonial-name-input"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Rating Field */}
                <div>
                  <Label className="font-heading font-semibold text-slate-700 tracking-wide mb-2 block">
                    Ocena *
                  </Label>
                  <div className="flex gap-2" data-testid="testimonial-rating">
                    {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
                  </div>
                </div>

                {/* Content Field */}
                <div>
                  <Label htmlFor="testimonial-content" className="font-heading font-semibold text-slate-700 tracking-wide mb-2 block">
                    Twoja opinia *
                  </Label>
                  <Textarea
                    id="testimonial-content"
                    rows={4}
                    placeholder="Opisz swoje doświadczenie z kursu..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className={`w-full px-4 py-3 border-2 rounded-sm transition-colors resize-none ${
                      errors.content ? "border-red-400" : "border-slate-200 focus:border-yellow-400"
                    }`}
                    data-testid="testimonial-content-input"
                  />
                  {errors.content && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.content}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-testimonial-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                      Wysyłanie...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Wyślij opinię
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default TestimonialsSection;
